import { isRetryableError } from "@rakazo/core";
import type { LiveTranscriptTurn } from "./live-voice";

export type LiveHistoryStatus = "saved" | "saving" | "error";
export type LiveRecord = {
  callId: string;
  turn: number;
  role: "user" | "bot";
  text: string;
  interrupted?: boolean;
};
type Pending = { record: LiveRecord; attempts: number };
type Write = (record: LiveRecord, signal: AbortSignal) => Promise<unknown>;

/** In-memory retry of immutable transcript writes only; this never submits an agent task.
 * Повторює лише незмінні записи розмови в пам’яті, ніколи не запускає завдання агента. */
export class LiveTranscriptRecorder {
  private queue: Pending[] = [];
  private processing: Promise<void> | null = null;
  private abort = new AbortController();
  private closed = false;
  private failed = false;
  private capacityExceeded = false;
  private sequence = 0;
  constructor(
    private readonly callId: string,
    private readonly write: Write,
    private readonly onStatus: (status: LiveHistoryStatus) => void = () => undefined,
  ) {}

  enqueue(turn: LiveTranscriptTurn): void {
    if (this.closed || !turn.text) return;
    const pieces: string[] = [];
    let piece = "";
    for (const character of turn.text) {
      if (piece.length + character.length > 4000) {
        pieces.push(piece);
        piece = "";
      }
      piece += character;
    }
    if (piece) pieces.push(piece);
    if (this.queue.length + pieces.length > 100 || this.sequence + pieces.length > 10_000) {
      this.failed = true;
      this.capacityExceeded = true;
      this.onStatus("error");
      return;
    }
    for (const text of pieces)
      this.queue.push({
        record: {
          callId: this.callId,
          turn: this.sequence++,
          role: turn.role,
          text,
          ...(turn.interrupted ? { interrupted: true } : {}),
        },
        attempts: 0,
      });
    this.onStatus("saving");
    if (!this.failed) this.process();
  }
  retry(): Promise<void> {
    if (this.closed) return Promise.resolve();
    if (this.capacityExceeded)
      return Promise.reject(
        new Error("Live transcript capacity exceeded / Перевищена місткість голосового запису"),
      );
    this.failed = false;
    for (const pending of this.queue) pending.attempts = 0;
    this.onStatus("saving");
    this.process();
    return this.flush();
  }
  async flush(): Promise<void> {
    while (this.processing) await this.processing;
    if (this.failed || this.queue.length)
      throw Error(
        "Live conversation history is not saved / Історію голосової розмови не збережено",
      );
  }
  close(): void {
    this.closed = true;
    this.abort.abort();
    this.queue = [];
  }
  private process(): void {
    if (this.processing || this.closed) return;
    this.processing = this.drain().finally(() => {
      this.processing = null;
      if (this.queue.length && !this.failed && !this.closed) this.process();
    });
  }
  private async drain(): Promise<void> {
    while (this.queue.length && !this.closed && !this.failed) {
      const pending = this.queue[0]!;
      try {
        await this.write(
          pending.record,
          AbortSignal.any([this.abort.signal, AbortSignal.timeout(5_000)]),
        );
        if (this.closed) return;
        this.queue.shift();
      } catch (error) {
        if (this.closed) return;
        pending.attempts++;
        if (!isRetryableError(error) || pending.attempts >= 3) {
          this.failed = true;
          this.onStatus("error");
          return;
        }
        await new Promise<void>((resolve) => {
          const timer = setTimeout(finish, 250 * 2 ** (pending.attempts - 1));
          const signal = this.abort.signal;
          function finish() {
            clearTimeout(timer);
            signal.removeEventListener("abort", finish);
            resolve();
          }
          signal.addEventListener("abort", finish, { once: true });
          if (signal.aborted) finish();
        });
      }
    }
    if (!this.closed && !this.failed) this.onStatus("saved");
  }
}

let scope: string | null = null;
const recorders = new Map<string, { target: string; recorder: LiveTranscriptRecorder }>();
export function clearLiveRecorders(): void {
  for (const entry of recorders.values()) entry.recorder.close();
  recorders.clear();
  scope = null;
}
export function liveRecordScope(value: string | null): void {
  if (scope === value) return;
  clearLiveRecorders();
  scope = value;
}
export function registerLiveRecorder(
  currentScope: string,
  target: string,
  recorder: LiveTranscriptRecorder,
  callId: string,
): void {
  liveRecordScope(currentScope);
  if (!recorders.has(callId) && recorders.size >= 16)
    throw new RangeError(
      "Too many unsaved voice conversations / Забагато незбережених голосових розмов",
    );
  recorders.set(callId, { target, recorder });
}
export function forgetLiveRecorder(callId: string, recorder: LiveTranscriptRecorder): void {
  if (recorders.get(callId)?.recorder === recorder) recorders.delete(callId);
}
export async function flushLiveRecords(currentScope: string, target: string): Promise<void> {
  liveRecordScope(currentScope);
  for (const [id, entry] of recorders) {
    if (entry.target !== target) continue;
    await entry.recorder.flush();
    recorders.delete(id);
  }
}
